import { useMemo } from 'react'
import { SpinningLoader } from '@mc/design-system'
import { TrackingContainer } from '@mc/visibility'

import { reportError } from '@mc/next-newrelic'
import { ClassSampleEmailCapture } from 'components/EmailCapture'

import { FAQ } from '../FAQ'
import { InstructorCarousels } from '../InstructorCarousels'
import { PlansSection } from '../PlansSection'
import { AccessAllClasses } from '../AccessAllClasses'
import { BelowTheFoldDataProvider, usePageData } from '../PageDataProvider'

import { useCoursesByCategory } from '../../dataFetching'
import { useCourseMappingDeprecated } from '../../dataFetching/useCourseMapping'
import { trackCatalogPageRowViewed } from '../../events'

export const BelowTheFold = () => {
  const { slug } = usePageData()
  const { coursesByCategory, isValidating, error } = useCoursesByCategory(slug)
  const { courseMapping, isLoading: isValCourseMap } =
    useCourseMappingDeprecated(
      (coursesByCategory || [])?.filter(
        <T extends { slug: string }>(
          course: T | null | undefined,
        ): course is T => course?.slug !== undefined,
      ),
    )

  const sortedCoursesByCategory = useMemo(() => {
    const coursesWherePrimary: typeof coursesByCategory = []
    const coursesWhereSecondary: typeof coursesByCategory = []
    coursesByCategory?.forEach((course) => {
      const courseMeta = courseMapping?.[course.slug]
      if (courseMeta?.primaryCategory === slug) {
        coursesWherePrimary.push(course)
      } else {
        coursesWhereSecondary.push(course)
      }
    })
    return [...coursesWherePrimary, ...coursesWhereSecondary]
  }, [courseMapping, coursesByCategory, slug])

  if (isValidating || isValCourseMap) {
    return (
      <div
        className='col-12'
        style={{
          position: 'relative',
          minHeight: 500,
        }}
      >
        <SpinningLoader />
      </div>
    )
  }
  if (error || !sortedCoursesByCategory) {
    reportError(error, {
      message: 'Error fetching courses by category',
      isCoursesByCategoryFalsy: String(!coursesByCategory),
    })
    return null
  }

  return (
    <BelowTheFoldDataProvider
      value={{
        content: sortedCoursesByCategory,
      }}
    >
      <div>
        <InstructorCarousels />
        <PlansSection />
        <FAQ />
        <AccessAllClasses />
        <EmailCapture />
      </div>
    </BelowTheFoldDataProvider>
  )
}

const EmailCapture = () => {
  const { slug } = usePageData()

  return (
    <TrackingContainer
      onFirstVisible={() =>
        trackCatalogPageRowViewed({
          rowName: 'email_collection_section',
          categorySlug: slug,
        })
      }
    >
      <ClassSampleEmailCapture
        trackProps={{
          contentType: 'category',
          location: 'category_page_body_slim_banner',
          slug,
          category: slug,
        }}
        preSelectedCategorySlug={slug}
        categoryEmailCaptureClass='mc-mt-9'
      />
    </TrackingContainer>
  )
}
